import { useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Sparkle, ArrowRight } from "@phosphor-icons/react";
import { Link } from "react-router-dom";

export default function Scoper() {
  const [problem, setProblem] = useState("");
  const [industry, setIndustry] = useState("");
  const [constraints, setConstraints] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const run = async (e) => {
    e.preventDefault();
    if (!problem.trim()) return;
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post("/ai/scope", { problem, industry, constraints });
      setResult(data.result);
    } catch {
      toast.error("Scoper unavailable. Try again or use the Project Router.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="scoper-page" className="px-6 md:px-14 py-16 md:py-24">
      <div className="max-w-5xl grid md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <div className="text-overline mb-4">◢ AI scoper</div>
          <h1 className="font-display font-bold tracking-tighter text-4xl sm:text-5xl text-white leading-[1.02]">
            Describe it.
            <br />
            Get a <span className="text-[#FF4500]">plan</span>.
          </h1>
          <p className="mt-5 text-[#8E8E93] text-[15px]">
            Wisewit's solutions architect — running on GPT-5.2 with our domain priors.
            Outputs modules, approach, stack, risks, and a complexity rating.
          </p>

          <form onSubmit={run} className="mt-10 space-y-5" data-testid="scoper-form">
            <div>
              <label className="ww-label">Problem</label>
              <textarea
                data-testid="scoper-problem"
                value={problem}
                onChange={(e) => setProblem(e.target.value)}
                rows={6}
                required
                placeholder="e.g., We inspect bottle caps manually; 12s per cap, defects slip through under bad light."
                className="ww-input resize-none"
              />
            </div>
            <div>
              <label className="ww-label">Industry (optional)</label>
              <input data-testid="scoper-industry" value={industry} onChange={(e) => setIndustry(e.target.value)} className="ww-input" placeholder="e.g., Beverage manufacturing" />
            </div>
            <div>
              <label className="ww-label">Constraints (optional)</label>
              <input data-testid="scoper-constraints" value={constraints} onChange={(e) => setConstraints(e.target.value)} className="ww-input" placeholder="e.g., on-prem only, < $200k, 3 months" />
            </div>
            <button data-testid="scoper-submit" type="submit" disabled={loading} className="btn-primary mt-2">
              <Sparkle size={14} weight="fill" /> {loading ? "Scoping…" : "Generate plan"}
            </button>
          </form>
        </div>

        <div className="md:col-span-7" data-testid="scoper-result">
          {!result && !loading && (
            <div className="ww-card p-10 min-h-[60vh] flex items-center justify-center text-center">
              <div>
                <div className="text-overline">◢ Awaiting input</div>
                <div className="mt-4 font-display text-2xl text-[#8E8E93]">Your generated plan will appear here.</div>
              </div>
            </div>
          )}

          {loading && (
            <div className="ww-card p-10 min-h-[60vh] flex flex-col items-center justify-center gap-4">
              <div className="w-2 h-2 rounded-full bg-[#DFFF00] ww-pulse" />
              <div className="font-mono text-[11px] tracking-[0.2em] uppercase text-[#8E8E93]">analyzing problem</div>
            </div>
          )}

          {result && (
            <div className="ww-card p-8" data-testid="scope-output">
              <div className="text-overline mb-3">◢ Summary</div>
              <p className="font-display text-xl text-white leading-snug">{result.summary}</p>

              <div className="mt-8 grid grid-cols-2 gap-6">
                <Block title="Recommended modules" items={result.recommended_modules} accent="#FF4500" />
                <Block title="Stack" items={result.stack} accent="#DFFF00" />
                <Block title="Approach" items={result.approach} accent="#FF4500" full />
                <Block title="Risks" items={result.risks} accent="#00E5FF" full />
              </div>

              <div className="mt-8 grid grid-cols-2 gap-px bg-[#2A2A2A] border border-[#2A2A2A]">
                <div className="bg-[#0a0a0a] p-5">
                  <div className="ww-num">Complexity</div>
                  <div className="font-display text-3xl text-white mt-1">{result.est_complexity}</div>
                </div>
                <div className="bg-[#0a0a0a] p-5">
                  <div className="ww-num">Next step</div>
                  <div className="text-white text-sm mt-2">{result.next_step}</div>
                </div>
              </div>

              <div className="mt-8">
                <Link to="/contact" className="btn-primary">
                  Hand this to a Wisewit engineer <ArrowRight size={14} weight="bold" />
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Block({ title, items, accent, full }) {
  if (!items?.length) return null;
  return (
    <div className={full ? "col-span-2" : ""}>
      <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#8E8E93] mb-3">{title}</div>
      <ul className="space-y-2">
        {items.map((it, i) => (
          <li key={i} className="flex items-start gap-2 text-sm text-[#F5F5F5]">
            <span className="w-1 h-1 rounded-full mt-2 flex-shrink-0" style={{ background: accent }} />
            <span>{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
